import React, { useCallback, useEffect, useRef, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import {
  Button,
  COLORS,
  Modal,
  SANS_SERIF_FONT_FAMILY,
  SERIF_FONT_FAMILY,
  UnstyledButton,
} from '@clutter/clean';

import { XButton } from '@root/components/icons/x_button';

// Shared modal container styles
export const ModalContainer = styled.div`
  background: ${COLORS.cloud};
  border-radius: 24px;
  padding: 18px 24px 24px;
  width: calc(100vw - 32px);
  max-width: 480px;

  @media (min-width: 768px) {
    padding: 18px 36px 24px;
  }
`;

// Close button row - positioned at top right of modal
const CloseButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 5px;
  margin: -5px -5px 0 0;
`;

// Close button styling
const CloseButton = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.panther};

  &:hover {
    opacity: 0.7;
  }
`;

// Combined close button with icon - use this for simplicity
interface ModalCloseButtonProps {
  onClose: () => void;
  'aria-label'?: string;
  disabled?: boolean;
}

export const ModalCloseButton: React.FC<ModalCloseButtonProps> = ({
  onClose,
  'aria-label': ariaLabel = 'Close',
  disabled = false,
}) => (
  <CloseButtonRow>
    <CloseButton
      type="button"
      onClick={disabled ? undefined : onClose}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-disabled={disabled}
    >
      <XButton width="14" height="14" strokeWidth={1.75} />
    </CloseButton>
  </CloseButtonRow>
);

export const PillButton = styled(Button)`
  border-radius: 36px;
`;

// Modal wrapper that sets backgroundColor="transparent" by default
interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onClose,
  children,
}) => (
  <Modal
    isOpen={isOpen}
    handleModalClose={onClose}
    backgroundColor="transparent"
  >
    {children}
  </Modal>
);

// Bottom sheet container with swipe-down-to-close functionality
const BaseBottomSheetContainer = styled.div<{ translateY?: number }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${COLORS.cloud};
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  padding: 18px 24px calc(24px + env(safe-area-inset-bottom, 0px));
  max-height: 85vh;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.1);
  touch-action: pan-y;
  transform: ${({ translateY = 0 }) => `translateY(${translateY}px)`};
  transition: ${({ translateY = 0 }) =>
    translateY === 0 ? 'transform 0.2s ease-out' : 'none'};

  @media (min-width: 600px) {
    position: relative;
    border-radius: 24px;
    max-width: 480px;
    margin: 0 auto;
    max-height: 85vh;
    transform: none;
  }
`;

const MobileCloseButtonWrapper = styled.div`
  display: block;

  @media (min-width: 600px) {
    display: none;
  }
`;

interface SwipeableBottomSheetProps {
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  /** Omit top-right close on small viewports (backdrop / swipe still close). */
  hideMobileCloseButton?: boolean;
}

/** Funnel modal headings — Recoleta Medium 28px / 1.2 (Figma Headers/H4) */
const funnelModalHeadingStyle = css`
  font-family: ${SERIF_FONT_FAMILY};
  font-size: 28px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  color: ${COLORS.panther};
  font-feature-settings:
    'liga' off,
    'clig' off;
`;

// Primary modal title (h2)
export const ModalTitle = styled.h2`
  ${funnelModalHeadingStyle}
  margin: 0;
`;

/** Same visual as ModalTitle; use h3 for secondary sections inside one dialog (a11y). */
export const ModalSectionHeading = styled.h3`
  ${funnelModalHeadingStyle}
  margin: 0;
`;

/** Funnel modal body — Basier Regular 18px / 1.2 (Figma Paragraph/Body Medium) */
export const ModalBodyParagraph = styled.p`
  margin: 0;
  font-family: ${SANS_SERIF_FONT_FAMILY};
  font-size: 18px;
  font-weight: 400;
  line-height: 1.2;
  color: ${COLORS.panther};
`;

/** Vertical rhythm between Q&A blocks (Figma gap 36px) */
export const ModalCopyStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  width: 100%;
  text-align: left;
`;

/** Heading + body cluster (Figma gap 12px) */
export const ModalCopySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
`;

export const ModalScrollableBody = styled.div`
  max-height: min(600px, 75vh);
  overflow-y: auto;
  width: 100%;
`;

export const SwipeableBottomSheet: React.FC<SwipeableBottomSheetProps> = ({
  onClose,
  children,
  className,
  hideMobileCloseButton = false,
}) => {
  const touchStartY = useRef<number | null>(null);
  const touchStartTime = useRef<number | null>(null);
  const [translateY, setTranslateY] = useState(0);
  const SWIPE_THRESHOLD = 100; // pixels
  const SWIPE_VELOCITY_THRESHOLD = 0.3; // pixels per ms

  // Lock body scroll when bottom sheet is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    setTranslateY(0); // Reset any previous translation
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (touchStartY.current === null) return;
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - touchStartY.current;
    const element = e.currentTarget as HTMLElement;

    // If swiping down
    if (deltaY > 0) {
      // If at the top of the scrollable area, allow swipe-to-dismiss
      if (element.scrollTop === 0) {
        e.preventDefault();
        e.stopPropagation();
        // Visual feedback: move the sheet down as user drags (no cap - can drag all the way off)
        setTranslateY(deltaY);
      }
    } else {
      // If swiping up, allow normal scrolling
      setTranslateY(0);
    }
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartY.current === null || touchStartTime.current === null) {
        setTranslateY(0);
        return;
      }

      const touchEndY = e.changedTouches[0].clientY;
      const touchEndTime = Date.now();
      const deltaY = touchEndY - touchStartY.current;
      const deltaTime = touchEndTime - touchStartTime.current;
      const velocity = Math.abs(deltaY / deltaTime);

      // Close if swiped down enough or with sufficient velocity
      if (
        deltaY > SWIPE_THRESHOLD ||
        (deltaY > 50 && velocity > SWIPE_VELOCITY_THRESHOLD)
      ) {
        onClose();
      } else {
        // Snap back if not enough swipe
        setTranslateY(0);
      }

      touchStartY.current = null;
      touchStartTime.current = null;
    },
    [onClose],
  );

  return (
    <BaseBottomSheetContainer
      className={className}
      translateY={translateY}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {!hideMobileCloseButton ? (
        <MobileCloseButtonWrapper>
          <ModalCloseButton onClose={onClose} />
        </MobileCloseButtonWrapper>
      ) : null}
      {children}
    </BaseBottomSheetContainer>
  );
};
